import { buildCsvFromRows } from '../../../shared/csv-core/infra/buildCsv'
import { parseCsvText, rowToRecord } from '../../../shared/csv-core/infra/parseCsv';
import type { CatalogCsvEntityKind, CsvImportCommitResult } from '../../domain/csv/csvEntityKinds';
import {
  commitBrandRow,
  commitCategoryRow,
  commitProductRow,
  commitProductGroup,
  commitSupplierRow,
} from './catalogCsvImportApply';
import {
  loadBrandCandidates,
  loadCategoryCandidates,
  loadSupplierCandidates,
  resolvePerformedByUserId,
  type ParsedRow,
} from './catalogCsvImportHelpers';
import { previewCatalogCsvImport } from './catalogCsvImportPreview';
import { groupProductRowsBySku } from './catalogCsvVariantParse';

export { previewCatalogCsvImport } from './catalogCsvImportPreview';

export async function commitCatalogCsvImport(
  entity: CatalogCsvEntityKind,
  csvText: string,
  opts?: { maxProducts?: number | null; performedByUserId?: string | null; variantsEnabled?: boolean }
): Promise<CsvImportCommitResult> {
  const performedByUserId = resolvePerformedByUserId(opts?.performedByUserId);
  const preview = await previewCatalogCsvImport(entity, csvText, opts);
  if (preview.blocked) {
    throw new Error(preview.blockMessage ?? 'Importación bloqueada.');
  }

  const parsed = parseCsvText(csvText);
  const errors: Array<{ rowNumber: number; message: string }> = [];
  let applied = 0;
  let skipped = 0;
  let failed = 0;
  let altas = 0;
  let ediciones = 0;

  const categoryCandidates = await loadCategoryCandidates();
  const brandCandidates = await loadBrandCandidates();
  const supplierCandidates = await loadSupplierCandidates();

  if (entity === 'products') {
    // #712: Agrupar filas por SKU del parent para procesar variantes.
    const allRows: Array<{ rowNumber: number; record: ParsedRow }> = [];
    for (let i = 0; i < parsed.rows.length; i += 1) {
      const rowNumber = i + 2;
      const previewRowResult = preview.rows.find((r) => r.rowNumber === rowNumber);
      if (!previewRowResult || previewRowResult.status === 'error') {
        if (previewRowResult?.message) {
          errors.push({ rowNumber, message: previewRowResult.message });
        }
        failed += 1;
        continue;
      }
      if (previewRowResult.status === 'skip') {
        skipped += 1;
        continue;
      }
      const record = rowToRecord(parsed.headers, parsed.rows[i]) as ParsedRow;
      allRows.push({ rowNumber, record });
    }

    const groups = groupProductRowsBySku(allRows);
    for (const group of groups) {
      try {
        const groupResults = await commitProductGroup(group.rows, {
          categoryCandidates,
          brandCandidates,
          supplierCandidates,
          performedByUserId,
          variantsEnabled: opts?.variantsEnabled ?? false,
        });
        for (const r of groupResults) {
          if (r.outcome === 'error') {
            failed += 1;
            if (r.message) errors.push({ rowNumber: r.rowNumber, message: r.message });
          } else if (r.outcome === 'skipped') {
            skipped += 1;
          } else {
            applied += 1;
            if (r.outcome === 'created') altas += 1;
            else ediciones += 1;
          }
        }
      } catch (e) {
        failed += group.rows.length;
        for (const row of group.rows) {
          errors.push({ rowNumber: row.rowNumber, message: e instanceof Error ? e.message : 'Error de grupo.' });
        }
      }
    }

    return { applied, skipped, failed, errors, altas, ediciones };
  }

  for (let i = 0; i < parsed.rows.length; i += 1) {
    const rowNumber = i + 2;
    const previewRowResult = preview.rows.find((r) => r.rowNumber === rowNumber);
    if (!previewRowResult || previewRowResult.status === 'error') {
      if (previewRowResult?.message) {
        errors.push({ rowNumber, message: previewRowResult.message });
      }
      failed += 1;
      continue;
    }
    if (previewRowResult.status === 'skip') {
      skipped += 1;
      continue;
    }

    const record = rowToRecord(parsed.headers, parsed.rows[i]) as ParsedRow;

    try {
      if (entity === 'categories') {
        const outcome = await commitCategoryRow(record, categoryCandidates);
        applied += 1;
        if (outcome === 'created') altas += 1;
        else ediciones += 1;
        continue;
      }
      if (entity === 'brands') {
        const outcome = await commitBrandRow(record, brandCandidates);
        applied += 1;
        if (outcome === 'created') altas += 1;
        else ediciones += 1;
        continue;
      }
      if (entity === 'suppliers') {
        const outcome = await commitSupplierRow(record, supplierCandidates);
        applied += 1;
        if (outcome === 'created') altas += 1;
        else ediciones += 1;
        continue;
      }
    } catch (e) {
      failed += 1;
      errors.push({
        rowNumber,
        message: e instanceof Error ? e.message : 'No se pudo aplicar la fila.',
      });
    }
  }

  return {
    applied,
    skipped,
    failed,
    errors,
    altas,
    ediciones,
  };
}

export function buildCatalogImportErrorsCsv(
  errors: Array<{ rowNumber: number; message: string }>
): string {
  return buildCsvFromRows(
    ['fila', 'mensaje'],
    errors.map((e) => [String(e.rowNumber), e.message])
  );
}
